/**
 * useProjectsStore — the active-project context (FLY3D-938).
 *
 * The dashboard is project-scoped: every data view reads `activeProject` and the
 * router guard bounces to the Project Selection gate when it is null. The active
 * project itself persists in sessionStorage (survives reload, cleared on tab
 * close so a fresh login re-gates). The per-user "last used" history lives in
 * localStorage so "Continue where you left off" works across sessions.
 */
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { Project } from '@/types/projects'

const LAST_USED_KEY = 'idi-fly:project-last-used'

interface LastUsedFile {
  version: 1
  /** userId (Cognito sub) → last active project + per-project last-open times. */
  users: Record<string, { lastProjectId: string; usedAt: Record<string, string> }>
}

function readLastUsed(): LastUsedFile {
  try {
    const raw = localStorage.getItem(LAST_USED_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.version === 1 && parsed.users) return parsed as LastUsedFile
    }
  } catch {
    /* corrupt entry — start fresh */
  }
  return { version: 1, users: {} }
}

function writeLastUsed(file: LastUsedFile) {
  try {
    localStorage.setItem(LAST_USED_KEY, JSON.stringify(file))
  } catch {
    /* storage full/blocked — history is best-effort */
  }
}

export const useProjectsStore = defineStore(
  'projects',
  () => {
    const activeProject = ref<Project | null>(null)

    /** The scoping key every data query/create sends (null before selection). */
    const activeProjectId = computed(() => activeProject.value?.projectId ?? null)

    /** Select a project and remember it as the user's last-used one. */
    const setActiveProject = (project: Project, userId: string | null) => {
      activeProject.value = project
      if (!userId) return
      const file = readLastUsed()
      const entry = file.users[userId] ?? { lastProjectId: '', usedAt: {} }
      entry.lastProjectId = project.projectId
      entry.usedAt[project.projectId] = new Date().toISOString()
      file.users[userId] = entry
      writeLastUsed(file)
    }

    /** Drop the active project (logout / access revoked) — the guard re-gates. */
    const clearActiveProject = () => {
      activeProject.value = null
    }

    /** The user's last active projectId, for "Continue where you left off". */
    const lastProjectIdFor = (userId: string | null): string | null =>
      (userId && readLastUsed().users[userId]?.lastProjectId) || null

    /** When this user last opened a project (ISO) — drives the Recent-activity sort. */
    const lastUsedAt = (userId: string | null, projectId: string): string | null =>
      (userId && readLastUsed().users[userId]?.usedAt?.[projectId]) || null

    return { activeProject, activeProjectId, setActiveProject, clearActiveProject, lastProjectIdFor, lastUsedAt }
  },
  { persist: { storage: sessionStorage } },
)
